import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth.store';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  LayoutDashboard,
  Server,
  Plus,
  CreditCard,
  Settings,
  Bell,
  LogOut,
  Menu,
  Puzzle,
  Users,
  ChevronDown,
  Check,
  CheckCheck,
  ExternalLink,
  User as UserIcon,
  PenSquare,
} from 'lucide-react';
import { cn, formatRelativeTime } from '@/lib/utils';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

const coreNavItems = [
  { key: 'overview', path: '/dashboard', icon: LayoutDashboard, labelKey: 'dashboard.overview' },
  { key: 'instances', path: '/dashboard/instances', icon: Server, labelKey: 'dashboard.instances' },
  { key: 'new', path: '/dashboard/new', icon: Plus, labelKey: 'dashboard.newInstance' },
  { key: 'skills', path: '/dashboard/skills', icon: Puzzle, labelKey: 'skills.browse' },
  { key: 'billing', path: '/dashboard/billing', icon: CreditCard, labelKey: 'common.billing' },
];

const devToolsNavItems = [
  // Disabled: Marketplace, Webhooks, API Keys - not needed for current release
  // { key: 'marketplace', path: '/dashboard/marketplace', icon: Store, labelKey: 'marketplace.title' },
  { key: 'teams', path: '/dashboard/teams', icon: Users, labelKey: 'teams.title' },
  // { key: 'webhooks', path: '/dashboard/webhooks', icon: Webhook, labelKey: 'webhooks.title' },
  // { key: 'api-keys', path: '/dashboard/api-keys', icon: Key, labelKey: 'apiKeys.title' },
];

export function DashboardLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [devToolsOpen, setDevToolsOpen] = useState(true);

  const queryClient = useQueryClient();

  const { data: notificationsData } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const { data } = await api.get('/notifications?limit=5');
      return data;
    },
    refetchInterval: 30000,
  });

  const unreadCount = notificationsData?.unreadCount || 0;

  const markRead = useMutation({
    mutationFn: (id: string) => api.patch(`/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => api.patch('/notifications/read-all'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
    },
  });

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="flex h-screen bg-muted/30">
      {/* Sidebar */}
      <aside className={cn(
        'fixed inset-y-0 left-0 z-50 w-64 bg-background border-r transform transition-transform md:relative md:translate-x-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full',
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2 h-16 px-6 border-b hover:bg-accent/50 transition-colors">
            <img src="/logo.png" alt="SetOpenClaw" className="h-14 w-14 rounded-xl object-contain" />
            <span className="text-lg font-bold">SetOpenClaw</span>
          </Link>

          {/* Nav Items */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {/* Core navigation */}
            {coreNavItems.map((item) => {
              const isActive = item.path === '/dashboard'
                ? location.pathname === '/dashboard'
                : location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.key}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {t(item.labelKey)}
                </Link>
              );
            })}

            {/* Developer Tools — collapsible section */}
            <div className="pt-3">
              <button
                onClick={() => setDevToolsOpen(!devToolsOpen)}
                className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
              >
                {t('nav.developerTools')}
                <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', !devToolsOpen && '-rotate-90')} />
              </button>
              {devToolsOpen && devToolsNavItems.map((item) => {
                const isActive = location.pathname.startsWith(item.path);
                const isDisabled = item.key === 'teams'; // Disable Teams

                if (isDisabled) {
                  return (
                    <div
                      key={item.key}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium opacity-40 cursor-not-allowed"
                    >
                      <item.icon className="h-4 w-4" />
                      {t(item.labelKey)}
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.key}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                    )}
                  >
                    <item.icon className="h-4 w-4" />
                    {t(item.labelKey)}
                  </Link>
                );
              })}
            </div>

            {/* Settings — always at bottom of nav */}
            <div className="pt-3">
              <Link
                to="/dashboard/settings"
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors',
                  location.pathname.startsWith('/dashboard/settings')
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )}
              >
                <Settings className="h-4 w-4" />
                {t('common.settings')}
              </Link>
            </div>

            {/* Admin section */}
            {user?.role === 'ADMIN' && (
              <>
                <div className="pt-4 pb-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3">Admin</p>
                </div>
                <Link
                  to="/admin"
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors',
                    location.pathname === '/admin'
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  )}
                >
                  <Users className="h-4 w-4" />
                  Users
                </Link>
                <Link
                  to="/admin/instances"
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors',
                    location.pathname === '/admin/instances'
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  )}
                >
                  <Server className="h-4 w-4" />
                  Instances
                </Link>
                <Link
                  to="/admin/blog"
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors',
                    location.pathname === '/admin/blog'
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  )}
                >
                  <PenSquare className="h-4 w-4" />
                  Blog
                </Link>
              </>
            )}
          </nav>

          {/* User section — logout lives in the header profile dropdown */}
          <div className="p-4 border-t space-y-3">
            <LanguageSwitcher variant="compact" />
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-semibold">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="flex items-center justify-between h-16 px-6 border-b bg-background">
          <button
            className="md:hidden p-2 -ml-2 text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="relative p-2 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-accent">
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[380px] p-0">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <h3 className="font-semibold text-sm">{t('notifications.title')}</h3>
                  {unreadCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => markAllRead.mutate()}
                      className="h-7 text-xs gap-1.5"
                    >
                      <CheckCheck className="h-3.5 w-3.5" />
                      {t('notifications.markAllRead')}
                    </Button>
                  )}
                </div>

                {/* Notifications list */}
                {!notificationsData?.notifications?.length ? (
                  <div className="flex flex-col items-center py-12 px-4">
                    <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center mb-3">
                      <Bell className="h-6 w-6 text-muted-foreground/40" />
                    </div>
                    <p className="text-sm text-muted-foreground">{t('notifications.empty')}</p>
                  </div>
                ) : (
                  <>
                    <div className="max-h-[400px] overflow-y-auto">
                      <div className="divide-y">
                        {notificationsData.notifications.map((notif: any) => (
                          <div
                            key={notif.id}
                            className={cn(
                              'flex items-start gap-3 px-4 py-3 hover:bg-accent/50 transition-colors cursor-pointer',
                              !notif.isRead && 'bg-primary/5',
                            )}
                          >
                            <div className={cn('mt-1.5 h-2 w-2 rounded-full flex-shrink-0', notif.isRead ? 'bg-transparent' : 'bg-emerald-500')} />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm leading-tight">{notif.title}</p>
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{notif.message}</p>
                              <p className="text-xs text-muted-foreground mt-1.5">{formatRelativeTime(notif.createdAt || notif.created_at)}</p>
                            </div>
                            {!notif.isRead && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markRead.mutate(notif.id);
                                }}
                                className="p-1.5 text-muted-foreground hover:text-emerald-600 transition-colors"
                                title="Mark as read"
                              >
                                <Check className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Footer - See all link */}
                    <DropdownMenuSeparator />
                    <div className="p-2">
                      <Link to="/dashboard/notifications" className="block">
                        <Button variant="ghost" className="w-full justify-center gap-2 text-xs h-8">
                          {t('notifications.viewAll')}
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      </Link>
                    </div>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Profile dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-semibold transition-colors hover:bg-primary/20 focus:outline-none focus:ring-2 focus:ring-primary/30"
                  aria-label="Open profile menu"
                >
                  {user?.name?.charAt(0)?.toUpperCase() || <UserIcon className="h-4 w-4" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none truncate">
                      {user?.name}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground truncate">
                      {user?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/dashboard/settings" className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>{t('common.settings')}</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t('common.signOut')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
